import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Modal } from '~/components/overlays/Modal';
import { ModalHeader } from '~/components/overlays/ModalHeader';
import { Button } from '~/components/ui';
import { SellerContactsRow } from '~/components/common/SellerContactsRow';
import {
  SellerOrganizationPicker,
  type PickableSellerOrganization,
} from '~/components/common/SellerOrganizationPicker';
import { useSellerRecipients } from '~/pages/buy-lists/sections/request-for-quote/useSellerRecipients';
import {
  useGetRequestForQuoteFullyPriced,
  useSubmitRequestForQuote,
} from '~/api/requestForQuote';
import { useAddRequestForQuoteToCart } from '~/api/order';
import { useSubmitOnce } from '~/hooks/useSubmitOnce';
import { toggleId } from '~/utils/toggleId';
import { toastGraphQLError } from '~/utils';
import type { SellerInventoryForBuyer } from '~/types/inventory';
import { FacilityPicker } from './FacilityPicker';

type SelectSellersForRandomTallyModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  platformProductId: string;
  shippingLocationId: string;
  excludeOrganizationIds: string[];
  requestForQuoteId: string;
};

export function SelectSellersForRandomTallyModal({
  isOpen,
  onClose,
  onSubmitted,
  platformProductId,
  shippingLocationId,
  excludeOrganizationIds,
  requestForQuoteId,
}: Readonly<SelectSellersForRandomTallyModalProps>) {
  const { t } = useTranslation('order-builder');

  const recipients = useSellerRecipients<PickableSellerOrganization>();
  const { selectedOrganizationIds, selectedOrganizations, sellerInvitations } =
    recipients;
  const [expandedOrganizationIds, setExpandedOrganizationIds] = useState<
    Set<string>
  >(() => new Set());

  const toggleExpand = (organizationId: string) => {
    setExpandedOrganizationIds((previous) =>
      toggleId(previous, organizationId)
    );
  };

  const { data: requestForQuoteData } =
    useGetRequestForQuoteFullyPriced(requestForQuoteId);
  const { mutate: submitRequestForQuote, isPending: isSubmittingRequest } =
    useSubmitRequestForQuote();
  const { mutate: addRequestForQuoteToCart, isPending: isAddingToCart } =
    useAddRequestForQuoteToCart();
  const submitOnce = useSubmitOnce();
  const isSubmitting = isSubmittingRequest || isAddingToCart;

  const isFullyPriced =
    requestForQuoteData?.requestForQuote?.fullyPriced === true;

  const [selectedSeller] = selectedOrganizations;
  const selectedOrganization = selectedSeller?.organization;

  let addToCartDisabledReason: string | undefined;
  if (selectedOrganizationIds.size !== 1) {
    addToCartDisabledReason = t(
      'order_builder.create_random_tally.add_to_cart_disabled_reason'
    );
  } else if (!isFullyPriced) {
    addToCartDisabledReason = t(
      'order_builder.create_random_tally.add_to_cart_unpriced_reason'
    );
  }

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  const handleAddToCart = (inventory: SellerInventoryForBuyer) => {
    const recipientUserIds = selectedSeller
      ? selectedSeller.contacts.map((contact) => contact.id)
      : [];
    submitOnce((onSettled) => {
      addRequestForQuoteToCart(
        {
          requestForQuoteId,
          facilityId: inventory.facility.id,
          recipientUserIds,
        },
        {
          onSuccess: () => {
            toast.success(
              t('order_builder.create_random_tally.add_to_cart_success')
            );
            onSubmitted();
          },
          onError: toastGraphQLError,
          onSettled,
        }
      );
    });
  };

  const handleSubmit = () => {
    if (selectedOrganizationIds.size === 0) {
      toast.error(
        t('order_builder.create_random_tally.at_least_one_seller_required')
      );
      return;
    }

    submitOnce((onSettled) => {
      submitRequestForQuote(
        { id: requestForQuoteId, sellerInvitations },
        {
          onSuccess: () => {
            toast.success(t('order_builder.create_random_tally.success'));
            onSubmitted();
          },
          onError: toastGraphQLError,
          onSettled,
        }
      );
    });
  };

  const rowLabels = {
    contactCount: (count: number) =>
      t('order_builder.create_random_tally.contact_count', { count }),
    noContacts: t('order_builder.create_random_tally.no_contacts'),
    selectedCount: (count: number) =>
      t('order_builder.create_random_tally.selected_count', { count }),
    selectOrganization: (name: string) =>
      t('order_builder.create_random_tally.select_organization', { name }),
  };

  const facilityPickerBaseFilter = useMemo(
    () => ({ destinationLocationId: shippingLocationId }),
    [shippingLocationId]
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose}>
        <div className="bg-neut-light rounded-lg shadow-xl max-w-7xl w-full h-[min(600px,90vh)] flex flex-col">
          <ModalHeader
            title={t('order_builder.create_random_tally.sellers_heading')}
            onClose={handleClose}
          />

          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-4">
            <SellerOrganizationPicker
              testId="random-tally-sellers"
              labels={{
                searchPlaceholder: t(
                  'order_builder.create_random_tally.search_placeholder'
                ),
                searchLabel: t(
                  'order_builder.create_random_tally.search_label'
                ),
                loading: t(
                  'order_builder.create_random_tally.loading_organizations'
                ),
                error: t(
                  'order_builder.create_random_tally.sellers_load_failed'
                ),
                emptyTitle: t(
                  'order_builder.create_random_tally.no_organizations_title'
                ),
                emptyMessage: t(
                  'order_builder.create_random_tally.no_organizations'
                ),
                noResultsTitle: t(
                  'order_builder.create_random_tally.no_organization_matches_title'
                ),
                noResultsMessage: t(
                  'order_builder.create_random_tally.no_organization_matches'
                ),
              }}
              excludeOrganizationIds={excludeOrganizationIds}
              renderOrganization={(organization, listContact) => (
                <SellerContactsRow
                  key={organization.id}
                  organization={organization}
                  recipients={recipients}
                  labels={rowLabels}
                  testId={`random-tally-seller-${organization.id}`}
                  checkboxIdPrefix={`random-tally-contact-${organization.id}`}
                  expanded={expandedOrganizationIds.has(organization.id)}
                  onToggleExpand={() => toggleExpand(organization.id)}
                  onContactAdded={listContact}
                  addContactDisabled={isSubmitting}
                />
              )}
            />
          </div>

          <div className="px-6 py-4 border-t border-neut-border flex items-center justify-between gap-3 shrink-0">
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t('order_builder.create_random_tally.back')}
            </Button>
            <div className="flex items-center gap-3">
              <Button
                variant="primary"
                width="auto"
                className="shrink-0"
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                <span className="whitespace-nowrap">
                  {t('order_builder.create_random_tally.request_quote')}
                </span>
              </Button>
              <FacilityPicker
                organizationId={selectedOrganization?.id ?? ''}
                platformProductId={platformProductId}
                facilityCount={selectedOrganization?.facilities.length ?? 0}
                baseFilter={facilityPickerBaseFilter}
                disabled={!!addToCartDisabledReason || isSubmitting}
                disabledReason={addToCartDisabledReason}
                accessibleLabel={t(
                  'order_builder.create_random_tally.add_to_cart'
                )}
                label={t('order_builder.create_random_tally.add_to_cart')}
                bypassPricedGate
                onSelectFacility={handleAddToCart}
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
